/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';
import { useDebouncedCallback } from 'use-debounce';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronLeftLargeIcon from '@atlaskit/icon/core/migration/chevron-left--chevron-left-large';
import ChevronRightLargeIcon from '@atlaskit/icon/core/migration/chevron-right--chevron-right-large';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const atlaskitCssStyles = cssMap({
	containerStyles: {
		display: 'flex',
		position: 'relative',
		alignItems: 'center',
	},
	hoverButtonStyle: {
		position: 'absolute',
		zIndex: 400,
		cursor: 'pointer',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
	},
});

const leftStyle = css({
	background: `linear-gradient(90deg, ${token('elevation.surface.overlay')} 0%, ${token('elevation.surface.overlay')} 80%, rgba(255,0,0,0) 100%)`,
	left: '0',

	'&:hover': {
		paddingRight: token('space.100'),
	},
});

const rightStyle = css({
	background: `linear-gradient(270deg, ${token('elevation.surface.overlay')} 0%, ${token('elevation.surface.overlay')} 80%, rgba(255,0,0,0) 100%)`,
	right: '0',

	'&:hover': {
		paddingLeft: token('space.100'),
	},
});

const scrollContainerStyle = css({
	minWidth: 'space.0',
	display: 'flex',
	overflowX: 'auto',
	overflowY: 'hidden',
	gap: token('space.100'),
});

const OFFSET = 250;

const isScrollAtLeft = (elem: HTMLDivElement | null) => elem?.scrollLeft === 0;
const isScrollAtRight = (elem: HTMLDivElement | null) =>
	elem
		? // https://stackoverflow.com/questions/3898130/check-if-a-user-has-scrolled-to-the-bottom-not-just-the-window-but-any-element
			Math.abs(elem?.scrollWidth - elem?.scrollLeft - elem?.clientWidth) <= 1
		: false;

export const HorizontalScrollContainer = ({ children }: { children: React.ReactNode }) => {
	const ref = useRef<HTMLDivElement>(null);
	const { formatMessage } = useIntl();

	const [shouldHideLeft, setShouldHideLeft] = useState(false);
	const [shouldHideRight, setShouldHideRight] = useState(false);

	const [onScroll] = useDebouncedCallback(() => {
		setShouldHideLeft(isScrollAtLeft(ref.current));
		setShouldHideRight(isScrollAtRight(ref.current));
	}, 10);

	useEffect(() => {
		setShouldHideLeft(isScrollAtLeft(ref.current));
		setShouldHideRight(isScrollAtRight(ref.current));
	}, []);

	return (
		<Inline xcss={atlaskitCssStyles.containerStyles}>
			{!shouldHideLeft && (
				<div css={[atlaskitCssStyles.hoverButtonStyle, leftStyle]}>
					<IconButton
						shape="circle"
						appearance="subtle"
						icon={(iconProps) => <ChevronLeftLargeIcon {...iconProps} size="small" />}
						isTooltipDisabled
						label={formatMessage(messages.scrollLeft)}
						onClick={() => {
							ref.current?.scrollBy({
								top: 0,
								left: -OFFSET,
								behavior: 'smooth',
							});
						}}
					/>
				</div>
			)}
			<div ref={ref} css={scrollContainerStyle} onScroll={onScroll}>
				{children}
			</div>
			{!shouldHideRight && (
				<div css={[atlaskitCssStyles.hoverButtonStyle, rightStyle]}>
					<IconButton
						shape="circle"
						appearance="subtle"
						icon={(iconProps) => <ChevronRightLargeIcon {...iconProps} size="small" />}
						isTooltipDisabled
						label={formatMessage(messages.scrollRight)}
						onClick={() => {
							ref.current?.scrollBy({
								top: 0,
								left: OFFSET,
								behavior: 'smooth',
							});
						}}
					/>
				</div>
			)}
		</Inline>
	);
};
